import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, photoUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar, Search, Newspaper, ArrowRight } from "lucide-react";

const FALLBACK_COVER = "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&q=80&auto=format&fit=crop";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("es-DO", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return ""; }
}

function stripHtml(html) {
  if (!html) return "";
  if (typeof document === "undefined") return html.replace(/<[^>]*>/g, " ");
  // Add spaces between block elements before stripping so titles don't glue to paragraphs.
  const withSpaces = html.replace(/<\/(p|h2|h3|h4|li|blockquote|div)>/gi, "</$1> ");
  const div = document.createElement("div");
  div.innerHTML = withSpaces;
  return (div.textContent || "").replace(/\s+/g, " ").trim();
}

export default function News() {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeCategory) params.category = activeCategory;
      if (q) params.q = q;
      const [{ data: a }, { data: c }] = await Promise.all([
        api.get("/news", { params }),
        categories.length ? Promise.resolve({ data: categories }) : api.get("/news/categories"),
      ]);
      setArticles(a);
      if (!categories.length) setCategories(c);
    } catch {
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeCategory]);

  const onSearch = (e) => { e.preventDefault(); load(); };

  return (
    <div className="mx-auto max-w-7xl px-5 md:px-10 pt-6 md:pt-16 pb-16" data-testid="news-page">
      <div className="mb-8 md:mb-10">
        <div className="label-eyebrow text-secondary inline-flex items-center gap-2">
          <Newspaper className="h-3 w-3" /> Editorial to2casa
        </div>
        <h1 className="mt-2 font-serif text-[2rem] leading-[1.05] sm:text-4xl md:text-6xl">Noticias Inmobiliarias</h1>
        <p className="mt-3 text-sm md:text-base text-muted-foreground max-w-2xl">
          Análisis, tendencias y consejos del mercado inmobiliario dominicano publicados por el equipo editorial de to2casa.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-8 pb-6 border-b border-border">
        <form onSubmit={onSearch} className="flex-1 min-w-[220px] max-w-md flex gap-2">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar artículos…"
              className="pl-9 rounded-full"
              data-testid="news-search-input"
            />
          </div>
          <Button type="submit" variant="secondary" className="rounded-full" data-testid="news-search-btn">Buscar</Button>
        </form>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setActiveCategory("")}
            data-testid="cat-all"
            className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-widest border transition-colors ${activeCategory === "" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-accent"}`}
          >Todas</button>
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setActiveCategory(c)}
              data-testid={`cat-${c}`}
              className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-widest border transition-colors ${activeCategory === c ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-accent"}`}
            >{c}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-muted-foreground text-sm">Cargando artículos…</div>
      ) : articles.length === 0 ? (
        <div className="py-24 text-center border border-dashed border-border rounded-sm" data-testid="news-empty">
          <Newspaper className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">Aún no hay artículos publicados{activeCategory ? ` en “${activeCategory}”` : ""}.</p>
        </div>
      ) : (
        <div className="grid gap-6 md:gap-8 sm:grid-cols-2 lg:grid-cols-3" data-testid="news-grid">
          {articles.map((a) => (
            <Link
              to={`/noticias/${a.slug || a.article_id}`}
              key={a.article_id}
              className="group block"
              data-testid={`news-card-${a.article_id}`}
            >
              <div className="aspect-[16/10] overflow-hidden rounded-sm bg-muted mb-4">
                <img
                  src={a.cover_photo ? photoUrl(a.cover_photo) : FALLBACK_COVER}
                  alt={a.title}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2 flex-wrap">
                <span className="uppercase tracking-widest text-secondary font-semibold">{a.category}</span>
                <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {formatDate(a.published_at)}</span>
              </div>
              <h2 className="font-serif text-xl md:text-2xl leading-tight group-hover:text-primary transition-colors break-words">{a.title}</h2>
              <p className="mt-2 text-sm text-muted-foreground line-clamp-3 break-words">
                {stripHtml(a.content).slice(0, 180)}
              </p>
              <div className="mt-3 inline-flex items-center gap-1 text-sm text-primary opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all">
                Leer más <ArrowRight className="h-3.5 w-3.5" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
